import { SearchResponse } from '@/src/types/flight'
import { getSplitId } from '../searchUtils/getSplitId'

/**
 * Which per-card price the filter labels reflect:
 * - 'multiAirlinePrice' (default): combined/incremental RT price — solution view.
 * - 'splitLegFare': the leg's INDIVIDUAL fare — RT split view (so filter prices match the cards).
 */
export type LegPriceField = 'multiAirlinePrice' | 'splitLegFare'

const cardPrice = (card: any, priceField: LegPriceField) =>
    (card?.priceBreakup?.[priceField] ?? card?.priceBreakup?.pr) || 0

export const stopsFilterPriceModifier = (
    flightResultResponseData: SearchResponse,
    leg: 0 | 1,
    priceField: LegPriceField = 'multiAirlinePrice'
) => {
    const targetCards = flightResultResponseData?.cards?.[leg]
    const targetStopsFilter = flightResultResponseData?.filterData?.NewStops?.[leg]
    const targetPriceUpSort = flightResultResponseData?.sortData?.price_up?.[leg]

    targetStopsFilter?.options?.forEach((stopType, stopTypeIndex) => {
        const targetCardId = targetPriceUpSort?.find((cardId, cardIdIndex) => {
            const cardIndex = getSplitId(cardId)?.[1]
            const card = targetCards?.[Number(cardIndex)]
            const maxStopsInSectors = card?.maxStopsInSectors
            return maxStopsInSectors === stopType
        })
        if (targetCardId) {
            const targetCard = targetCards?.[Number(getSplitId(targetCardId)?.[1])]
            targetStopsFilter.prices[stopTypeIndex] = cardPrice(targetCard, priceField)
        }
    })
}

export const airlineFilterPriceModifier = (
    flightResultResponseData: SearchResponse,
    leg: 0 | 1,
    priceField: LegPriceField = 'multiAirlinePrice'
) => {
    const targetCards = flightResultResponseData?.cards?.[leg]
    const targetAirlineFilter = flightResultResponseData?.filterData?.airlineFilter?.[leg]
    const targetPriceUpSort = flightResultResponseData?.sortData?.price_up?.[leg]

    targetAirlineFilter?.options?.forEach((airline, airlineIndex) => {
        const targetCardId = targetPriceUpSort?.find((cardId, cardIdIndex) => {
            const cardIndex = getSplitId(cardId)?.[1]
            const card = targetCards?.[Number(cardIndex)]
            const airlineCodes = card.airlineCodes
            return airlineCodes.includes(airline.value)
        })
        if (targetCardId) {
            const targetCard = targetCards[Number(getSplitId(targetCardId)?.[1])]
            targetAirlineFilter.options[airlineIndex].priceLabel = cardPrice(targetCard, priceField)
        }
    })
}

export const priceRangeFilterPriceModifier = (
    flightResultResponseData: SearchResponse,
    leg: 0 | 1,
    priceField: LegPriceField = 'multiAirlinePrice'
) => {
    const targetCards = flightResultResponseData?.cards?.[leg]
    const targetPriceRangeFilter = flightResultResponseData?.filterData?.journeyPrice?.[leg]
    const targetPriceUpSort = flightResultResponseData?.sortData?.price_up?.[leg]
    const minPriceCardId = getSplitId(targetPriceUpSort?.[0])?.[1]
    const minPriceCard = targetCards?.[Number(minPriceCardId)]
    const minPrice = cardPrice(minPriceCard, priceField)
    const maxPriceCardId = getSplitId(targetPriceUpSort?.[targetPriceUpSort.length - 1])?.[1]
    const maxPriceCard = targetCards?.[Number(maxPriceCardId)]
    const maxPrice = cardPrice(maxPriceCard, priceField)

    targetPriceRangeFilter.min = minPrice || 0
    targetPriceRangeFilter.max = maxPrice || 0
    targetPriceRangeFilter.current.max = maxPrice || 0
    targetPriceRangeFilter.current.min = minPrice || 0
}
